import MenuBookIcon from "@mui/icons-material/MenuBook";
import { Box, Button, Drawer, Typography } from "@mui/material";
import { useState } from "react";
import { Helmet } from "react-helmet";
import { useParams } from "react-router-dom";
import { colorOverrides as colors } from "theme/colorOverrides";
import { findDoc } from "./docsManifest";
import { DocsLanding } from "./DocsLanding";
import { DocsPage } from "./DocsPage";
import { DocsSidebar } from "./DocsSidebar";
import { DOCS_BORDER } from "./docsStyles";

// Mobile first: on a phone the doc takes the full width and the nav lives in a drawer; from md up
// the nav sits beside the doc.
export const Docs = () => {
  const slug = (useParams()["*"] ?? "").replace(/\/+$/, "");
  const [drawerOpen, setDrawerOpen] = useState(false);

  // /home/docs is the landing page that points at the Cloud and Conductor docs.
  if (!slug) {
    return (
      <>
        <Helmet>
          <title>Orkes Cloud - Docs</title>
        </Helmet>
        <DocsLanding />
      </>
    );
  }

  const title = findDoc(slug)?.title;

  return (
    <>
      <Helmet>
        <title>{`Orkes Cloud - Docs${title ? ` - ${title}` : ""}`}</title>
      </Helmet>
      {/* SectionContainer's fixed 20px padding is a lot of a phone's width. */}
      <Box
        sx={{
          // No bottom padding: the footer brings its own 24px margin-top (App.css).
          paddingTop: { xs: 3, md: 4 },
          paddingX: { xs: 3, md: 4 },
          // Fill the space between header and footer, so a short doc's card still reaches the
          // bottom and the pager sits there rather than mid-page.
          minHeight: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { md: "flex-start" },
            gap: { xs: 3, md: 8 },
            width: "100%",
            // Fill the page, but stop on ultrawide monitors so lines stay readable.
            maxWidth: 1920,
          }}
        >
          <Button
            variant="outlined"
            startIcon={<MenuBookIcon />}
            onClick={() => setDrawerOpen(true)}
            aria-label="Browse docs"
            sx={{
              display: { xs: "flex", md: "none" },
              justifyContent: "flex-start",
              textTransform: "none",
              minHeight: 44,
              borderRadius: 2,
              borderColor: DOCS_BORDER,
              color: colors.gray02,
              backgroundColor: "#fff",
            }}
          >
            <Typography
              component="span"
              noWrap
              sx={{ fontSize: 15, minWidth: 0 }}
            >
              {title ?? "Browse docs"}
            </Typography>
          </Button>
          <Drawer
            anchor="left"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            sx={{ display: { md: "none" } }}
            PaperProps={{
              sx: { width: "85vw", maxWidth: 320, px: 3, py: 6 },
            }}
          >
            <DocsSidebar
              activeSlug={slug}
              onNavigate={() => setDrawerOpen(false)}
            />
          </Drawer>

          <Box
            sx={{
              display: { xs: "none", md: "block" },
              width: 280,
              flexShrink: 0,
              position: "sticky",
              top: 24,
              maxHeight: "calc(100vh - 48px)",
              overflowY: "auto",
            }}
          >
            <DocsSidebar activeSlug={slug} />
          </Box>
          <Box
            component="article"
            sx={{
              flex: 1,
              alignSelf: "stretch",
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
              minHeight: "350px",
              backgroundColor: "#fff",
              border: `1px solid ${DOCS_BORDER}`,
              borderRadius: 3,
              // The pager band inside runs edge to edge, so clip it to the rounded corners.
              overflow: "hidden",
            }}
          >
            <DocsPage key={slug} slug={slug} />
          </Box>
        </Box>
      </Box>
    </>
  );
};
